import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, TextField } from '@material-ui/core';
import TranslateIcon from '@material-ui/icons/Translate';
import { tr } from '@/common/utils/tr';
import MuiTooltip from '@/component/common/MuiTooltip';
import TranslationManagementConstant from '../TranslationManagementConstant';

/**
 * Expanded detail of one 原文: its per-language translations (docType=ROW).
 * Loaded lazily when the grid row opens.
 *
 * A translated language is edited inline: double-click the text to turn it into an
 * input and blur to save (no 編集/保存 buttons; ESC cancels). An untranslated
 * language cannot be typed directly - it must be auto-translated first via the
 * 翻訳 action, matching the design. Language codes and translated text are DATA -
 * never wrapped with tr().
 */
export default function TranslationRowDetail({
    loading,
    rows,
    editingRowKey,
    editText,
    editError,
    onEditTextChange,
    onStartEdit,
    onCancelEdit,
    onSubmitEdit,
    onTranslate,
    submitting
}) {
    // Set on ESC so the blur it triggers cancels instead of saving.
    const cancelRef = React.useRef(false);

    const renderStatus = (status, hasText) => {
        const displayStatus = TranslationManagementConstant.STATUS_DISPLAY_ALIAS[status] || status;
        const code = hasText ? displayStatus : 'UNTRANSLATED';
        const meta = TranslationManagementConstant.STATUS_META[code];
        if (!meta) {
            return <span className="tm-st tm-st-none">—</span>;
        }
        // Status is a defined label -> tr() (group-1 UI text).
        return <span className={`tm-st ${meta.cls}`}>{tr(meta.label)}</span>;
    };

    if (loading) {
        return (
            <div className="tm-detail-inner">
                <div className="tm-loading">
                    <CircularProgress size={18} />
                </div>
            </div>
        );
    }

    return (
        <div className="tm-detail-inner">
            <table>
                <thead>
                    <tr>
                        <th style={{ width: 110 }}>{tr('翻訳先言語')}</th>
                        <th>{tr('翻訳')}</th>
                        <th style={{ width: 100 }} className="tm-th-status">
                            {tr('状態')}
                        </th>
                        <th style={{ width: 44 }} />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={4} className="tm-empty">
                                {tr('該当するデータがありません')}
                            </td>
                        </tr>
                    ) : (
                        rows.map(row => {
                            const hasText = Boolean(row.translatedText);
                            const isEditing = editingRowKey === row.targetLanguage;
                            return (
                                <tr key={row.id || row.targetLanguage}>
                                    <td className="tm-lang">{row.targetLanguage}</td>
                                    <td className="tm-trans tm-ellipsis-cell">
                                        {isEditing ? (
                                            <TextField
                                                autoFocus
                                                fullWidth
                                                multiline
                                                variant="outlined"
                                                size="small"
                                                value={editText}
                                                onChange={e => onEditTextChange(e.target.value)}
                                                // Blur saves, unless ESC asked to cancel first
                                                // (empty / unchanged is a no-op in the handler).
                                                onBlur={() => {
                                                    if (cancelRef.current) {
                                                        cancelRef.current = false;
                                                        onCancelEdit();
                                                        return;
                                                    }
                                                    onSubmitEdit(row);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Escape') {
                                                        // Cancel without saving; blur reads the flag.
                                                        cancelRef.current = true;
                                                        e.target.blur();
                                                    }
                                                }}
                                                disabled={submitting}
                                                error={Boolean(editError)}
                                            />
                                        ) : hasText ? (
                                            // Double-click the text to edit it inline (same as the keyword of the parent row).
                                            <span
                                                className="tm-trans-text tm-ellipsis-text"
                                                onDoubleClick={() => !submitting && onStartEdit(row)}
                                            >
                                                {row.translatedText}
                                            </span>
                                        ) : (
                                            // 未翻訳: blank cell, still double-click to type a translation (§8.4).
                                            <span
                                                className="tm-untranslated-text"
                                                onDoubleClick={() => !submitting && onStartEdit(row)}
                                            >
                                                —
                                            </span>
                                        )}
                                        {isEditing && editError && <div className="tm-new-row-error">{editError}</div>}
                                    </td>
                                    <td className="tm-status-cell">
                                        {isEditing ? null : renderStatus(row.status, hasText)}
                                    </td>
                                    <td className="tm-row-actions">
                                        {/* The translate icon is on every row: an untranslated
                                            language is translated, a translated one is re-translated
                                            (overwritten). The cell itself is edited by double-click. */}
                                        {!isEditing && (
                                            <MuiTooltip title={tr('翻訳')}>
                                                <span className="tm-tooltip-wrap">
                                                    <button
                                                        type="button"
                                                        className="tm-icon-btn"
                                                        onClick={() => onTranslate(row)}
                                                        disabled={submitting}
                                                    >
                                                        <TranslateIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                </span>
                                            </MuiTooltip>
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}

TranslationRowDetail.propTypes = {
    loading: PropTypes.bool,
    rows: PropTypes.array,
    editingRowKey: PropTypes.string,
    editText: PropTypes.string,
    // Translated message shown under the input being edited ('' = none).
    editError: PropTypes.string,
    onEditTextChange: PropTypes.func.isRequired,
    onStartEdit: PropTypes.func.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    onSubmitEdit: PropTypes.func.isRequired,
    onTranslate: PropTypes.func.isRequired,
    submitting: PropTypes.bool
};

TranslationRowDetail.defaultProps = {
    loading: false,
    rows: [],
    editingRowKey: null,
    editText: '',
    editError: '',
    submitting: false
};
